import { defineStore, storeToRefs } from "pinia";
import {
  doc,
  query,
  collection,
  Timestamp,
  where,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db, converter } from "@/firebase";
import { getEnvCollection, getRootCollection } from "@/firebase/collections";
import { useFirestore } from "@vueuse/firebase";
import { useAuthStore } from "./auth";
import { useAdminStore } from "./admin";
import { computed, Ref, ref, watch } from "vue";
import { Environment, JobSheet, LineItem, LineItemFiles, Nullable } from "@/models";
import dayjs from "dayjs";
import { useRoute } from "vue-router";
import * as Sentry from "@sentry/vue";
import { shouldReportError } from "@/helpers/sentryErrors";

export const useOperatorAdminStore = defineStore("operator-admin", () => {
  const authStore = useAuthStore();
  const adminStore = useAdminStore();
  const { envId } = storeToRefs(adminStore);
  
  const _userId = ref<string | undefined>(localStorage.getItem('itv_op_uid') ?? undefined);
  watch(() => authStore.authenticatedUser?.uid, (uid) => {
    if (uid) {
      _userId.value = uid;
      localStorage.setItem('itv_op_uid', uid);
    }
  }, { immediate: true });
  const userId = computed(() => _userId.value);

  const route = useRoute();

  if (!envId.value && route) {
    envId.value = route.params?.envid as string;
  }
  if (!envId.value) {
    const cached = localStorage.getItem('itv_op_env');
    if (cached) envId.value = cached;
  }
  watch(() => envId.value, (id) => {
    if (id) localStorage.setItem('itv_op_env', id);
  }, { immediate: true });

  const userQuery = computed(
    () =>
      !!userId.value &&
      doc(
        db,
        getEnvCollection(envId.value!, 'OPERATORS'),
        userId.value
      ).withConverter(converter<OperatorUser>())
  );
  const operatorUser = useFirestore<OperatorUser>(userQuery);

  const isOperator = computed(() => {
    return !!operatorUser.value;
  })

  const envQuery = computed(
    () =>
      !!envId.value &&
      doc(db, getRootCollection('ENVIRONMENTS') + '/' + envId.value)
        .withConverter(converter<Environment>())
  );
  const environment = useFirestore<Environment>(envQuery);

  const selectedJobSheet: Ref<Nullable<JobSheet>> = ref(null);
  const selectedJobSheetId = ref<string | undefined>(undefined);

  watch(
    () => selectedJobSheet.value?.id,
    (id) => {
      if (id !== selectedJobSheetId.value) {
        selectedJobSheetId.value = id;
      }
    },
    { immediate: true }
  );

  function setSelectedJobSheetId(id?: string) {
    selectedJobSheetId.value = id;
  }

  function setSelectedJobSheet(job: JobSheet | null) {
    selectedJobSheetId.value = job?.id;
    selectedJobSheet.value = job;
  }

  const selectedJobSheetComputed = computed(() => {
    const allJobSheets = [...todaysJobSheets.value, ...upcomingJobSheets.value];
    return allJobSheets.find((js) => js.id === selectedJobSheetId.value) ?? null;
  });

  function getCreatedAtMillis(createdAt: unknown): number {
    if (!createdAt) {
      return 0;
    }

    if (createdAt instanceof Timestamp) {
      return createdAt.toMillis();
    }

    if (createdAt instanceof Date) {
      return createdAt.getTime();
    }

    if (typeof createdAt === "object" && createdAt !== null && "toDate" in (createdAt as any)) {
      const value = (createdAt as any).toDate?.();
      if (value instanceof Date) {
        return value.getTime();
      }
    }

    const parsed = dayjs(createdAt as any);
    return parsed.isValid() ? parsed.valueOf() : 0;
  }

  const fallbackSelectedJobSheetLineItems = computed(() => {
    const lineItems = selectedJobSheetComputed.value?.lineItems ?? [];
    return lineItems
      .filter((lineItem) => !lineItem.deleted)
      .sort((a, b) => {
        const aCreatedAt = getCreatedAtMillis(a.createdAt);
        const bCreatedAt = getCreatedAtMillis(b.createdAt);
        return aCreatedAt - bCreatedAt;
      });
  });

  const selectedJobSheetLineItems = computed(() =>
    fallbackSelectedJobSheetLineItems.value.filter((lineItem) => !lineItem.deleted)
  );

  const isSelectedJobSheetLineItemsLoading = computed(
    () =>
      !!selectedJobSheetId.value &&
      selectedJobSheetComputed.value === null &&
      fallbackSelectedJobSheetLineItems.value.length === 0
  );

  const recentJobsStart = computed(() =>
    Timestamp.fromDate(dayjs().subtract(1, 'day').startOf('day').toDate())
  );

  const operatorJobSheetsQuery = computed(() =>
    !!envId.value &&
    !!userId.value &&
    query(
      collection(db, getEnvCollection(envId.value, 'JOB_SHEETS')),
      where("operator.id", "==", userId.value),
      where("jobDate", ">=", recentJobsStart.value)
    ).withConverter(converter<JobSheet>())
  );

  const operatorJobSheetsRaw = useFirestore<JobSheet>(operatorJobSheetsQuery);

  const todaysJobSheetsQuery = computed(() => {
    const yesterday = Timestamp.fromDate(dayjs().subtract(1, 'day').startOf('day').toDate());
    const endOfToday = Timestamp.fromDate(dayjs().endOf('day').toDate());
    return [yesterday, endOfToday];
  });
  const todaysJobSheets = computed(() =>
    (operatorJobSheetsRaw.value ?? [])
      .filter((j) => {
        const [yesterday, endOfToday] = todaysJobSheetsQuery.value;
        return j.jobDate?.toMillis() >= yesterday.toMillis() && j.jobDate?.toMillis() <= endOfToday.toMillis();
      })
      ?.filter((j) => !["Completed", "Approved"].includes(j.status))
      .filter((j) => !j.deleted)
  );

  const upcomingJobSheetsQuery = computed(() =>
    Timestamp.fromDate(dayjs().add(1, 'day').hour(0).minute(0).second(0).toDate())
  );

  const upcomingJobSheets = computed(() =>
    (operatorJobSheetsRaw.value ?? [])
      .filter((j) => j.jobDate?.toMillis() > upcomingJobSheetsQuery.value.toMillis())
      ?.filter((j) => !["Completed", "Approved"].includes(j.status))
      .filter((j) => !j.deleted)
  );

  // my job sheets
  const myJobSheets = computed(() => todaysJobSheets.value);

  const myUpcomingJobSheets = computed(() => upcomingJobSheets.value);

  const isJobsLoading = computed(
    () => !!userId.value && !!envId.value && operatorJobSheetsRaw.value === undefined
  );

  // save partial jobsheet update
  async function updateJobSheet(jobsheet: Partial<JobSheet>) {
    if (!jobsheet.id) return;
    try {
      const operatorRef = doc(
        db,
        getEnvCollection(envId.value!, 'JOB_SHEETS'),
        jobsheet.id
      );
      const jobsheetUpdatePayload: Record<string, any> = {
        ...jobsheet,
        updatedAt: new Date(),
      };
      if (!jobsheetUpdatePayload.notes || jobsheetUpdatePayload.notes.length === 0) {
        delete jobsheetUpdatePayload.notes; // Remove notes if empty so we don't override on server
      }

      await updateDoc(operatorRef, jobsheetUpdatePayload);

      const lineItems = jobsheet.lineItems;

      const lineItemCollection = collection(
        db,
        getEnvCollection(envId.value!, 'JOB_SHEETS'),
        jobsheet.id,
        'lineItems'
      );

      if (lineItems && lineItems.length > 0) {
        const batch = writeBatch(db);

        for (const item of lineItems) {
          let lineItemId = item.id ?? '';

          if (item.id) {
            batch.set(doc(lineItemCollection, item.id), { ...item }, { merge: true });
          } else {
            const lineItemRef = doc(lineItemCollection);
            batch.set(doc(lineItemCollection, lineItemRef.id), {
              ...item,
              id: lineItemRef.id,
              createdAt: new Date(),
              deleted: false,
            });
            lineItemId = lineItemRef.id;
          }

          if (item.files && item.files.length > 0) {
            for (const file of item.files) {
              if (file.id) {
                batch.set(doc(lineItemCollection, lineItemId, 'files', file.id), { ...file }, { merge: true });
              } else {
                const fileRef = doc(collection(lineItemCollection, lineItemId, 'files'));
                batch.set(doc(lineItemCollection, lineItemId, 'files', fileRef.id), {
                  ...file,
                  id: fileRef.id,
                  lineItemId: lineItemId,
                  createdAt: new Date(),
                  deleted: false,
                });
              }
            }
          }
        }

        await batch.commit();
      }
    } catch (error) {
      console.error("Error updating job sheet:", error);
      if (shouldReportError(error)) {
        Sentry.captureException(error);
      }
      throw error;
    }
  }

  async function updateLineOrderPreference(lineOrderPreference: "oldest-first" | "newest-first") {
    if (!envId.value || !userId.value || !operatorUser.value) return;

    try {
      await updateDoc(
        doc(db, getEnvCollection(envId.value, 'OPERATORS'), userId.value),
        { lineOrderPreference }
      );
    } catch (error) {
      console.error("Error saving line-order preference:", error);
      if (shouldReportError(error)) {
        Sentry.captureException(error);
      }
    }
  }

  async function migrateJobSheetLineItems(jobsheetId: string, lineItemsToMigrate: LineItem[]): Promise<void> {
    if (!jobsheetId || !lineItemsToMigrate || lineItemsToMigrate.length === 0) return;

    try {
      const batch = writeBatch(db);
      const jobSheetRef = doc(db, getEnvCollection(envId.value!, 'JOB_SHEETS'), jobsheetId);
      const lineItemSubCollectionRef = collection(jobSheetRef, 'lineItems');

      const migratedLineItems: LineItem[] = [];

      for (const item of lineItemsToMigrate) {
        let lineItemDocRef;
        const itemDataForSubcollection: LineItem = { ...item };

        if (!itemDataForSubcollection.id) {

          lineItemDocRef = doc(lineItemSubCollectionRef);
          itemDataForSubcollection.id = lineItemDocRef.id;
          itemDataForSubcollection.createdAt = item.createdAt || new Date();
        } else {
          lineItemDocRef = doc(lineItemSubCollectionRef, itemDataForSubcollection.id);
        }
        itemDataForSubcollection.deleted = item.deleted || false;


        batch.set(lineItemDocRef, itemDataForSubcollection);


        if (itemDataForSubcollection.files && itemDataForSubcollection.files.length > 0) {
          for (const file of itemDataForSubcollection.files) {
            const fileDocRef = doc(collection(lineItemDocRef, 'files'));
            const fileDataForSubcollection: LineItemFiles = {
              ...file,
              id: fileDocRef.id,
              createdAt: file.createdAt || new Date(),
              lineItemId: itemDataForSubcollection.id,
              deleted: file.deleted || false,
            };
            batch.set(fileDocRef, fileDataForSubcollection);
          }
        }
        migratedLineItems.push(itemDataForSubcollection);
      }


      batch.update(jobSheetRef, {
        lineItems: migratedLineItems,
        lineItemsMigrated: true,
        updatedAt: new Date(),

        searchDocketNumbers: migratedLineItems
          .filter(li => !li.deleted)
          .map(li => li.docketNumber.trim().toLowerCase())
      });
      
      await batch.commit();
    } catch (error) {
      console.error(`Error migrating jobSheet ${jobsheetId} line items:`, error);
      Sentry.captureException(error);
      throw error; 
    }
  }

  return {
    userId,
    envId,
    operatorUser,
    selectedJobSheet,
    selectedJobSheetId,
    selectedJobSheetLineItems,
    isSelectedJobSheetLineItemsLoading,
    myJobSheets,
    myUpcomingJobSheets,
    isJobsLoading,
    environment,
    selectedJobSheetComputed,
    isOperator,
    setSelectedJobSheetId,
    setSelectedJobSheet,
    updateJobSheet,
    updateLineOrderPreference,
    migrateJobSheetLineItems
  };
});

export interface OperatorUser {
  id: string;
  email: string;
  enabled: boolean;
  firstName: string;
  lastName: string;
  username: string;
  lineOrderPreference?: "oldest-first" | "newest-first";
}
